/**
 * Rich Text Editor Component
 * 
 * WYSIWYG editor with markdown sync for proposal editing
 * Uses Tiptap for rich text editing with Google Docs-like experience
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TurndownService from 'turndown';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Table as TableIcon,
  Undo,
  Redo,
} from 'lucide-react';
import { Button } from '../UI/Button';

export interface RichTextEditorProps {
  value: string; // Markdown content
  onChange: (markdown: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

// Initialize Turndown for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
});

// Configure Turndown to preserve citation markers
turndownService.addRule('citation', {
  filter: (node: Node) => {
    return node.nodeName === 'SPAN' && 
           (node as HTMLElement).textContent?.match(/\[Source \d+\]/) !== null;
  },
  replacement: (content: string) => content,
});

// Configure Turndown to handle horizontal rules
turndownService.addRule('horizontalRule', {
  filter: 'hr',
  replacement: () => '\n\n---\n\n',
});

// Configure Turndown to preserve line breaks
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '\n',
});

// Configure Turndown to handle empty paragraphs (convert to blank line for spacing)
turndownService.addRule('emptyParagraph', {
  filter: (node: Node) => {
    return node.nodeName === 'P' && 
           (!node.textContent || node.textContent.trim() === '');
  },
  replacement: () => '\n', // Preserve as blank line instead of removing
});

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter report content in Markdown...',
  className = '',
  minHeight = '600px',
}: RichTextEditorProps) {
  // Convert markdown to HTML for Tiptap
  const markdownToHTML = useCallback((markdown: string): string => {
    if (!markdown) return '';
    
    try {
      // Split into lines for better processing
      const lines = markdown.split('\n');
      const htmlLines: string[] = [];
      let inList = false;
      let inCodeBlock = false;
      let codeBlockContent: string[] = [];
      let currentParagraph: string[] = [];
      
      const flushParagraph = () => {
        if (currentParagraph.length > 0) {
          // Join with newlines to preserve line breaks, then process
          const paraText = currentParagraph.join('\n').trim();
          if (paraText) {
            // Process inline formatting
            let processed = paraText
              // Bold
              .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
              // Italic (but not bold)
              .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>')
              // Citations (preserve exactly)
              .replace(/\[Source (\d+)\]/g, '<span class="citation">[Source $1]</span>')
              // Links
              .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
              // Inline code
              .replace(/`([^`]+)`/g, '<code>$1</code>');
            // Convert line breaks to <br> within paragraphs
            processed = processed.replace(/\n/g, '<br>');
            // Add margin-bottom for spacing between paragraphs
            htmlLines.push(`<p style="margin-bottom: 1em;">${processed}</p>`);
          }
          currentParagraph = [];
        }
      };
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        
        // Handle code blocks
        if (trimmed.startsWith('```')) {
          flushParagraph();
          if (inCodeBlock) {
            // End code block
            htmlLines.push(`<pre><code>${codeBlockContent.join('\n')}</code></pre>`);
            codeBlockContent = [];
            inCodeBlock = false;
          } else {
            // Start code block
            inCodeBlock = true;
          }
          continue;
        }
        
        if (inCodeBlock) {
          codeBlockContent.push(line);
          continue;
        }
        
        // Handle horizontal rules (--- or \---)
        if (trimmed === '---' || trimmed === '\\---' || trimmed.match(/^[-]{3,}$/)) {
          flushParagraph();
          htmlLines.push('<hr />');
          continue;
        }
        
        // Headings
        if (trimmed.match(/^###\s+(.+)$/)) {
          flushParagraph();
          htmlLines.push(`<h3 style="margin-top: 1.5em; margin-bottom: 0.75em;">${trimmed.replace(/^###\s+/, '')}</h3>`);
          continue;
        }
        if (trimmed.match(/^##\s+(.+)$/)) {
          flushParagraph();
          htmlLines.push(`<h2 style="margin-top: 1.75em; margin-bottom: 0.875em;">${trimmed.replace(/^##\s+/, '')}</h2>`);
          continue;
        }
        if (trimmed.match(/^#\s+(.+)$/)) {
          flushParagraph();
          htmlLines.push(`<h1 style="margin-top: 2em; margin-bottom: 1em;">${trimmed.replace(/^#\s+/, '')}</h1>`);
          continue;
        }
        
        // Lists
        if (trimmed.match(/^[-*]\s+(.+)$/)) {
          flushParagraph();
          if (!inList) {
            htmlLines.push('<ul>');
            inList = true;
          }
          htmlLines.push(`<li>${trimmed.replace(/^[-*]\s+/, '')}</li>`);
          continue;
        }
        if (trimmed.match(/^\d+\.\s+(.+)$/)) {
          flushParagraph();
          if (!inList) {
            htmlLines.push('<ol>');
            inList = true;
          }
          htmlLines.push(`<li>${trimmed.replace(/^\d+\.\s+/, '')}</li>`);
          continue;
        }
        
        // Close list if needed
        if (inList && trimmed === '') {
          htmlLines.push('</ul>');
          inList = false;
          continue;
        }
        
        // Empty line = paragraph break - preserve as empty paragraph for spacing
        if (trimmed === '') {
          flushParagraph();
          // Add empty paragraph to preserve spacing (will be converted back to blank line)
          htmlLines.push('<p></p>');
          continue;
        }
        
        // Add to current paragraph
        currentParagraph.push(line);
      }
      
      // Flush any remaining paragraph
      flushParagraph();
      
      // Close any open lists
      if (inList) {
        htmlLines.push('</ul>');
      }
      
      return htmlLines.join('');
    } catch (error) {
      console.error('Markdown to HTML conversion error:', error);
      return markdown;
    }
  }, []);

  // Convert HTML to markdown for storage
  const htmlToMarkdown = useCallback((html: string): string => {
    if (!html) return '';
    
    try {
      // Use Turndown to convert HTML to markdown
      let markdown = turndownService.turndown(html);
      
      // Clean up issues by processing line by line
      const lines = markdown.split('\n');
      const cleanedLines: string[] = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const prevLine = i > 0 ? lines[i - 1] : '';
        
        // Skip duplicate "Executive Summary: Section Name" patterns after section headers
        if (prevLine && prevLine.match(/^##\s+(.+)$/)) {
          const headingTitle = prevLine.replace(/^##\s+/, '').trim();
          // Check if current line duplicates the heading with "Executive Summary:" prefix
          const execSummaryPattern = new RegExp(`^\\*\\*Executive Summary:\\s*${headingTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*$`, 'i');
          const execSummaryPattern2 = new RegExp(`^Executive Summary:\\s*${headingTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i');
          if (line.match(execSummaryPattern) || line.match(execSummaryPattern2)) {
            continue; // Skip this duplicate line
          }
        }
        
        cleanedLines.push(line);
      }
      
      markdown = cleanedLines.join('\n');
      
      // Minimal cleanup - just normalize separators and remove excessive blank lines
      // But preserve intentional spacing (up to 2 blank lines)
      markdown = markdown
        .replace(/\\---/g, '---') // Normalize escaped separators
        .replace(/---\n\n---/g, '---') // Remove duplicate separators
        .replace(/\n{4,}/g, '\n\n\n') // Max 3 newlines (preserve spacing)
        // Don't trim - preserve trailing newlines that user added
        .replace(/^\n+/, '') // Only remove leading newlines
        .replace(/\n+$/, '\n'); // Preserve at least one trailing newline if user added spacing
      
      return markdown;
    } catch (error) {
      console.error('HTML to Markdown conversion error:', error);
      return html; // Fallback: return HTML as-is
    }
  }, []);

  // Ref to track if we're updating programmatically (to prevent infinite loops)
  const isUpdatingFromProps = useRef(false);
  // Ref to track the last value we set to avoid unnecessary updates
  const lastValueRef = useRef<string>(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: markdownToHTML(value),
    onUpdate: ({ editor }) => {
      // Skip onChange if this update came from our programmatic setContent
      if (isUpdatingFromProps.current) {
        isUpdatingFromProps.current = false;
        return;
      }
      
      const html = editor.getHTML();
      const markdown = htmlToMarkdown(html);
      
      // Only call onChange if the markdown actually changed
      if (markdown !== lastValueRef.current) {
        lastValueRef.current = markdown;
        onChange(markdown);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none focus:outline-none min-h-[600px] p-4',
      },
    },
  });

  // Update editor content when value prop changes (external updates)
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentHTML = editor.getHTML();
      const expectedHTML = markdownToHTML(value);
      const currentMarkdown = htmlToMarkdown(currentHTML);
      
      // Only update if content actually changed (avoid infinite loops)
      if (value !== currentMarkdown && value !== lastValueRef.current) {
        isUpdatingFromProps.current = true;
        lastValueRef.current = value;
        editor.commands.setContent(expectedHTML);
      }
    }
  }, [value, editor, markdownToHTML, htmlToMarkdown]);

  if (!editor) {
    return (
      <div className={`border border-gray-300 rounded-lg p-4 ${className}`} style={{ minHeight }}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-theme-paper ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleBold().run();
          }}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleItalic().run();
          }}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleUnderline().run();
          }}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        
        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
        
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 1 }).run();
          }}
          className={editor.isActive('heading', { level: 1 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 2 }).run();
          }}
          className={editor.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          className={editor.isActive('heading', { level: 3 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </Button>
        
        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
        
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleBulletList().run();
          }}
          className={editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleOrderedList().run();
          }}
          className={editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        
        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
        
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-700' : ''}
          title="Insert Link"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
          }}
          title="Insert Table"
        >
          <TableIcon className="h-4 w-4" />
        </Button>
        
        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
        
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().undo().run();
          }}
          disabled={!editor.can().chain().focus().undo().run()}
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().redo().run();
          }}
          disabled={!editor.can().chain().focus().redo().run()}
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor Content */}
      <div style={{ minHeight }} className="overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      {/* Word Count (optional) */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
        {(() => {
          const text = editor.getText();
          const words = text.trim().split(/\s+/).filter(word => word.length > 0);
          return `${words.length} words`;
        })()}
      </div>
    </div>
  );
}

